import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '@/components/ui/chart';
import { Skeleton } from '@/components/ui/Skeleton';
import { Label, Pie, PieChart } from 'recharts';

export function AppPieChart({
  chartData,
  config = {},
  innerData,
  dataKey, // Default value if not provided
  nameKey, // Default value if not provided
  isLoading = false,
}: {
  chartData: any[];
  config?: ChartConfig;
  innerData?: { value: number | string; title: string };
  dataKey: string; // Optional prop for dataKey
  nameKey: string; // Optional prop for nameKey
  isLoading?: boolean;
}) {
  const data = chartData?.map(item => ({
    ...item,
    fill: `var(--color-${item[nameKey]})`,
  }));
  return (
    <>
      {isLoading ? (
        <div className="p-18">
          <div className="relative flex items-center justify-center">
            <Skeleton className="relative h-72 w-72 rounded-full bg-coal-200 after:absolute after:start-1/2 after:top-1/2 after:h-48 after:w-48 after:-translate-x-1/2 after:-translate-y-1/2 after:rounded-full after:bg-white after:content-['']" />
            <div className="absolute rounded-full border-[50px] border-transparent border-e-gray-200 p-24"></div>
            <div className="absolute start-1/2 top-1/2 flex -translate-x-1/2 -translate-y-1/2 flex-col items-center gap-2">
              <Skeleton className="h-6 w-12 rounded-md bg-coal-200" />
              <Skeleton className="h-6 w-24 rounded-md bg-coal-200" />
            </div>
          </div>
        </div>
      ) : (
        <>
          <ChartContainer config={config} className="mx-auto aspect-square max-h-[300px] max-w-[300px]">
            <PieChart>
              <ChartTooltip cursor={false} content={<ChartTooltipContent hideLabel />} />
              <Pie
                data={data}
                dataKey={dataKey} // Using the dynamic prop
                nameKey={nameKey} // Using the dynamic prop
                innerRadius={80}
                strokeWidth={10}
              >
                <Label
                  content={({ viewBox }) => {
                    if (viewBox && 'cx' in viewBox && 'cy' in viewBox) {
                      return (
                        <text
                          x={viewBox.cx}
                          y={viewBox.cy}
                          textAnchor="middle"
                          dominantBaseline="middle"
                        >
                          <tspan
                            x={viewBox.cx}
                            y={viewBox.cy}
                            className="fill-foreground text-4xl font-bold"
                          >
                            {innerData?.value || ''}
                          </tspan>
                          <tspan
                            x={viewBox.cx}
                            y={(viewBox.cy || 0) + 24}
                            className="fill-muted-foreground text-base"
                          >
                            {innerData?.title || ''}
                          </tspan>
                        </text>
                      );
                    }
                  }}
                />
              </Pie>
            </PieChart>
          </ChartContainer>
          {/* Labels section below the chart */}
          <div className="flex flex-wrap justify-center gap-4">
            {data?.map((item, index) => (
              <div key={index} className="flex items-center gap-2 capitalize">
                <div
                  className="h-4 w-4 rounded-md"
                  style={{ backgroundColor: config[item[nameKey]]?.color }}
                />
                <span className="text-sm font-medium">{item[nameKey]}</span>
                <span className="text-sm font-medium">{item[dataKey]}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </>
  );
}
